import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import t from "./translate";

const EVENT_NAME = "school-bridge:flash";
const EXIT_DURATION = 320;

const modeStyles = {
  success: {
    shell: "border-emerald-200 bg-emerald-50 text-emerald-800",
    icon: "fa-circle-check text-emerald-600"
  },
  error: {
    shell: "border-rose-200 bg-rose-50 text-rose-800",
    icon: "fa-circle-exclamation text-rose-600"
  },
  notice: {
    shell: "border-brand-200 bg-brand-50 text-brand-800",
    icon: "fa-circle-info text-brand-600"
  }
};

function generateId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const FlashMessage = forwardRef(function FlashMessage({ initialMessages = [] }, ref) {
  const [messages, setMessages] = useState([]);
  const initialisedRef = useRef(false);

  useImperativeHandle(ref, () => ({
    show(message, mode = "success", visibilityDuration = 6000) {
      enqueue(message, mode, visibilityDuration);
    }
  }));

  useEffect(() => {
    if (initialisedRef.current || initialMessages.length === 0) {
      return;
    }

    initialisedRef.current = true;
    initialMessages.forEach((entry) => {
      enqueue(entry.message, entry.mode, entry.visibilityDuration || 6000);
    });
  }, [initialMessages]);

  useEffect(() => {
    function handleFlashEvent(event) {
      const { message, mode = "success", visibilityDuration = 6000 } = event.detail || {};
      enqueue(message, mode, visibilityDuration);
    }

    window.addEventListener(EVENT_NAME, handleFlashEvent);
    return () => window.removeEventListener(EVENT_NAME, handleFlashEvent);
  }, []);

  function enqueue(message, mode = "success", visibilityDuration = 6000) {
    if (!message) {
      return;
    }

    const id = generateId();
    setMessages((current) => [...current, { id, message, mode, exiting: false }]);

    window.setTimeout(() => beginDismiss(id), visibilityDuration);
  }

  function beginDismiss(id) {
    setMessages((current) => current.map((message) => (
      message.id === id ? { ...message, exiting: true } : message
    )));

    window.setTimeout(() => {
      setMessages((current) => current.filter((message) => message.id !== id));
    }, EXIT_DURATION);
  }

  return (
    <div className="pointer-events-none fixed right-4 top-4 z-[1000] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-3">
      {messages.map((message) => {
        const styles = modeStyles[message.mode] || modeStyles.notice;

        return (
          <div
            key={message.id}
            className={`pointer-events-auto overflow-hidden rounded-2xl border px-4 py-3 shadow-[0_20px_50px_rgba(15,23,42,0.14)] backdrop-blur ${
              message.exiting ? "flash-toast-exit" : "flash-toast-enter"
            } ${styles.shell}`}
            role="status"
            aria-live="polite"
          >
            <div className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex h-8 w-8 flex-none items-center justify-center rounded-xl bg-white/80">
                <i className={`fa-solid ${styles.icon}`} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold leading-6">{message.message}</p>
              </div>
              <button
                type="button"
                onClick={() => beginDismiss(message.id)}
                className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-xl text-slate-400 transition hover:bg-white/80 hover:text-slate-700"
                aria-label={t("shared.flash_message.dismiss_notification")}
              >
                <i className="fa-solid fa-xmark text-sm" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
});

export function showFlashMessage(message, mode = "success", visibilityDuration = 6000) {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new CustomEvent(EVENT_NAME, {
    detail: { message, mode, visibilityDuration }
  }));
}

export default FlashMessage;
